import axiosInstance from "../../components/axiosInstance";

const CENSUS_BASE = "/market-intelligence/census";
const SUPPORTED_INCLUDES = new Set(["demographics", "derived"]);

function makeValidationError(message) {
  const error = new Error(message);
  error.status = 400;
  error.isValidationError = true;
  return error;
}

function assertPattern(value, pattern, label) {
  const normalized = String(value || "").trim();
  if (!pattern.test(normalized)) throw makeValidationError(`${label} invalido.`);
  return normalized;
}

function normalizeIncludes(include = []) {
  const items = Array.isArray(include)
    ? include
    : String(include || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

  const unique = Array.from(new Set(items));
  const unsupported = unique.filter((item) => !SUPPORTED_INCLUDES.has(item));
  if (unsupported.length) throw makeValidationError(`Include nao suportado: ${unsupported.join(", ")}.`);
  return unique;
}

function toQuery(params) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
}

function normalizeApiError(error) {
  const status = error?.response?.status || error?.status || 0;
  const message =
    error?.response?.data?.message ||
    error?.message ||
    "Nao foi possivel consultar a API censitaria.";
  const normalized = new Error(message);
  normalized.status = status;
  normalized.isValidationError = Boolean(error?.isValidationError);
  return normalized;
}

export async function getCensusSector(cdSetor, include = []) {
  try {
    const code = assertPattern(cdSetor, /^\d{15}$/, "cd_setor");
    const includes = normalizeIncludes(include);
    const qs = toQuery({ include: includes.join(",") });
    const { data } = await axiosInstance.get(`${CENSUS_BASE}/sectors/${code}${qs}`);
    return data?.sector || null;
  } catch (error) {
    throw normalizeApiError(error);
  }
}

export async function listCensusSectors(params = {}) {
  try {
    const query = {};
    if (params.cd_mun) query.cd_mun = assertPattern(params.cd_mun, /^\d{7}$/, "cd_mun");
    if (params.cd_uf) query.cd_uf = assertPattern(params.cd_uf, /^\d{2}$/, "cd_uf");
    if (!query.cd_mun && !query.cd_uf) throw makeValidationError("Informe cd_mun ou cd_uf.");
    if (query.cd_mun && query.cd_uf) throw makeValidationError("Use cd_mun ou cd_uf, nao ambos.");

    const limit = params.limit === undefined || params.limit === "" ? 100 : Number(params.limit);
    if (!Number.isInteger(limit) || limit < 1 || limit > 500) {
      throw makeValidationError("O limite deve estar entre 1 e 500.");
    }

    const includes = normalizeIncludes(params.include || []);
    query.limit = limit;
    query.cursor = params.cursor || "";
    query.include = includes.join(",");

    const { data } = await axiosInstance.get(`${CENSUS_BASE}/sectors${toQuery(query)}`);
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      limit: data?.limit || limit,
      next_cursor: data?.next_cursor || null,
    };
  } catch (error) {
    throw normalizeApiError(error);
  }
}

export async function getCensusMunicipalitySummary(cdMun) {
  try {
    const code = assertPattern(cdMun, /^\d{7}$/, "cd_mun");
    const { data } = await axiosInstance.get(`${CENSUS_BASE}/municipalities/${code}/summary`);
    return data?.summary || null;
  } catch (error) {
    throw normalizeApiError(error);
  }
}

export async function getCensusVariables(semanticGroup) {
  try {
    const group = String(semanticGroup || "").trim();
    if (group && !/^[a-z0-9_]{1,80}$/.test(group)) {
      throw makeValidationError("Grupo semantico invalido.");
    }
    const { data } = await axiosInstance.get(`${CENSUS_BASE}/variables${toQuery({ semantic_group: group })}`);
    return Array.isArray(data?.items) ? data.items : [];
  } catch (error) {
    throw normalizeApiError(error);
  }
}
